import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Redirect, Link } from "react-router-dom";
import VerificationOptions from "./VerificationOptions";
import ContactHelp from "./ContactHelp";
import EnterCode from "./EnterCode";
import MainContent from "./MainContent";
import { FadeUp } from "./AnimationLibrary";
import Alert from "./Alert";

export default function SelectVerificationOption(props) {
	// These rarely or never seem to vary together, so they are being broken out instead of a larger state object
	const [verificationOptions, setVerificationOptions] = useState([]);
	const [validated, setValidated] = useState(false);
	const [helpExpanded, setHelpExpanded] = useState(false);
	const [showEnterCode, setShowEnterCode] = useState(false);
	const [alertState, setAlertState] = useState({});
	const [optionsLoading, setOptionsLoading] = useState(false);
	const [optionLastSelected, setOptionLastSelected] = useState({});
	const [sendingCode, setSendingCode] = useState(false);
	const [verifyingCode, setVerifyingCode] = useState(false);
	const [sentCodeCounter, setSentCodeCounter] = useState(0);
	const [codeSendSuccess, setCodeSendSuccess] = useState(false);

	const alertStates = {
		errorDefaultMessage: {
			title: "Oops, something went wrong",
			message: "We had an error. Please try again.",
		},
		errorCheckConnection: {
			title: "Oops, something went wrong",
			message: "We had an error. Please check that you're connected to the internet and try again.",
		},
		errorGettingVerificationOptionsFromApi: {
			title: "Sorry, we couldn't get all of your verification options",
			message: "Please try again later or call the Service Desk.",
			type: "warning" // This error doesn't make the app "unusable", so a "warning" type is appropriate here
		},
		errorSendingVerificationCode: {
			title: "There was an issue sending your code",
			message: "This is on us. We had an issue while trying to send you a verification code. Please try again."
		},
		errorValidatingFromEnterCode: {
			title: "Sorry, we could not verify the code",
			message: "This could mean that the code was entered incorrectly or that it has expired.",
			buttonTextPrimary: "Try again",
			onClickPrimary: () => setAlertState({})
		},
		errorValidatingFromSentCode: {
			title: "Sorry, we could not verify the code",
			message: "This could mean that the code was entered incorrectly or that it has expired.",
			buttonTextPrimary: "Send me a new code",
			onClickPrimary: (e) => {
				handleSendNewCode(optionLastSelected);
				setAlertState({});
			},
			buttonTextSecondary: "Try again",
			onClickSecondary: () => setAlertState({})
		}		
	};

	const setAlertStateWithResponseDetail = (baseAlertState, response) => {
		setAlertState({
			...baseAlertState,
			messageDetail: JSON.stringify(response, null, 2)
		})
	};


	const handleCloseAlert = () => {
		setAlertState({});
	};

	const handleCloseHelp = () => {
		setHelpExpanded(false);
	};

	// An option for sending a code has been chosen OR re-triggered by
	const handleSelectOption = async option => {
		setCodeSendSuccess(false);
		setOptionLastSelected(option);

		if (option.type === "help") {
			setHelpExpanded(true);
			return;
		}

		if (option.type === "entercode") {
			setShowEnterCode(true);
			return;
		}

		setSendingCode(true);
		setShowEnterCode(true);
		const data = await props.apiService.send_verification_code(props.user.netid, option.id);
		setSentCodeCounter(sentCodeCounter + 1);
		setSendingCode(false);

		if (data.ok) setCodeSendSuccess(true);
		
		if (!data) {
			setAlertState(alertStates.errorCheckConnection);
			setShowEnterCode(false);
			return;
		}

		if (!data.ok) {
			setAlertStateWithResponseDetail(alertStates.errorSendingVerificationCode, data);
			setShowEnterCode(false);
			return;
		}
	};

	const handleVerify = async (code, optionUsed) => {
		setVerifyingCode(true);
		const result = await props.apiService.validate_verification_code(props.user.netid, code);
		setVerifyingCode(false);

		if (!result) {
			setAlertState(alertStates.errorCheckConnection);
			return;
		}

		if (!result.ok && optionUsed.type === 'entercode') {
			setAlertStateWithResponseDetail(alertStates.errorValidatingFromEnterCode, result);
			return;
		}

		if (!result.ok) {
			setAlertState({
				...alertStates.errorValidatingFromSentCode,
				message: `${alertStates.errorValidatingFromSentCode.message} You can send another code to ${optionUsed.value} or try entering the code again.`,
				messageDetail: JSON.stringify(result, null, 2)
			});
			return;
		}
		
		props.setUser(result.user);
		props.setToken(result.token);
		setValidated(true);
	}
	
	const handleSendNewCode = (optionUsed) => {
		handleSelectOption(optionUsed);
	}

	const handleClearUser = () => { // Clear user when selection "Not you?" link
		props.setUser({});
	}

	const handleShowVerificationOptions = e => {
		e.preventDefault();
		setShowEnterCode(false);
	}

	const loadVerificationOptions = useCallback(async netid => {
		setOptionsLoading(true);
		const data = await props.apiService.get_verification_options(netid);
		setOptionsLoading(false);
		
		if (!data.ok) {
			setVerificationOptions([]);
			setAlertStateWithResponseDetail(alertStates.errorGettingVerificationOptionsFromApi, data);
			return;
		}

		setVerificationOptions(
			data.result.map(option => ({
				type: option.type,
				value: option.value,
				id: option.verificationOptionId,
				label: option.verificationOptionId === "personalEmail" ? "Send code to Email" : ""
			}))
		);
	
	// passing some dependencies creates an infinite re-render loop
	// eslint-disable-next-line
	}, [props.apiService]);

	// useEffect() cannot be called "conditionally"
	// so useEffect() must be called before redirect
	// but loadVerificationOptions() is not called yet
	useEffect(() => { 
		let isSubscribed = true;
		if (isSubscribed && props.user !== null && props.user.netid) {
			loadVerificationOptions(props.user.netid); 
		}
		return () => isSubscribed = false;
	}, [props.user, loadVerificationOptions]);

	if (!props.user || !props.user.netid) return <Redirect push from="/selectverificationoptions" to={props.pwReset ? "/" : "/telluswhoyouare"} />;

	if (validated) {
		if(!props.pwReset) return <Redirect push from="/selectverificationoptions" to="/welcome" />
		return <Redirect push from="/selectverificationoptions" to="/setpassword" />;
	}

	return (
		<Router /* react-router-dom Link components must be used insite Router component */ >
			{alertState.message ? <Alert {...alertState} close={handleCloseAlert}/> : ''}
			<MainContent sideBar={true} user={props.user}>
				<FadeUp delay="0">
					<div className="heading-container mt-1r">
						<h2 className="main-heading mb-0">{props.user.displayName || "No DisplayName on file"}</h2>
						<p className="subheading mt-0">{props.user.deptClass || ""}</p>
						{!showEnterCode ? (
							<Link onClick={handleClearUser} to="/telluswhoyouare" className="arrow-link">
								<span className="left">&#xFFEA;</span>Not you?
							</Link>
						) : (
							<a href="/" onClick={handleShowVerificationOptions} className="arrow-link">
								<span className="left">&#xFFEA;</span>Try a different option?
							</a>
						)}
					</div>
				</FadeUp>
				{!showEnterCode ? (
					<VerificationOptions
						user={props.user}
						optionsLoading={optionsLoading}
						verificationOptions={verificationOptions}
						handleSelectOption={handleSelectOption}
					/>
				) : (
					<EnterCode
							alertState={alertState}
							optionUsed={optionLastSelected}
							handleVerify={handleVerify}
							handleSendNewCode={handleSendNewCode}
							sendingCode={sendingCode}
							verifyingCode={verifyingCode}
							sentCodeCounter={sentCodeCounter}
							codeSendSuccess={codeSendSuccess}
					/>
				)}
			</MainContent>
			<ContactHelp expanded={helpExpanded} handleDismiss={handleCloseHelp}/>
		</Router>
	);
};

